import { useSyncExternalStore, useState, useEffect } from "react";

// Need to be careful what we pull in here as this export is used in
// tests via @skilitics/nautilus-core-designer.
import * as auth from "@skilitics/auth-client";

import getAuthClient from "../auth-client.ts";
import type { FeatureSwitch } from "./features.ts";

/**
 * Check if the current user is authenticated.
 * Useful for `enabled` option of react-query hooks to prevent
 * re-fetching on logout.
 */
export function getIsAuthenticated(): boolean {
  return getAuthClient().stage === "authenticated";
}

export type LoginState = auth.AuthClientStage | "no-workspaces";

function isNoWorkspaces() {
  const client = getAuthClient();
  return client.apiTokenError instanceof auth.AuthApiError && client.apiTokenError.code === "no-workspaces";
}

export function getLoginState(): LoginState {
  return isNoWorkspaces() ? "no-workspaces" : getAuthClient().stage;
}

export function useLoginState(): LoginState {
  return useSyncExternalStore(getAuthClient().subscribe, getLoginState);
}

/**
 * Wait for the login state to settle, for example in route loaders (which render immediately)
 * Current transient states include login-challenge as it is used to defer completing restore
 * until expired login tokens are refreshed to validate them.
 */
export async function waitForLoginState(): Promise<Exclude<LoginState, "restoring" | "login-challenge">> {
  const client = getAuthClient();
  if (client.stage === "none" || client.stage === "authenticated") {
    return isNoWorkspaces() ? "no-workspaces" : client.stage;
  }
  return new Promise((resolve) => {
    const cleanup = client.subscribe(() => {
      if (client.stage === "none" || client.stage === "authenticated") {
        cleanup();
        resolve(isNoWorkspaces() ? "no-workspaces" : client.stage);
      }
    });
  });
}

export async function getAuthToken(): Promise<string> {
  return await getAuthClient().getApiToken();
}

/**
 * Get the current Cognito username. Use this for management actions in interact
 * to e.g. determine the current user in the returned data.
 */
export function getCognitoUsername(): string | null {
  const { loginClaims } = getAuthClient();
  return loginClaims ? (loginClaims["cognito:username"] as string) : null;
}

/**
 * React hook to get the current Cognito username.
 * @see getCognitoUsername
 */
export function useCognitoUsername(): string | null {
  return useSyncExternalStore(getAuthClient().subscribe, getCognitoUsername);
}

/**
 * Shorthand to get the current workspace.
 * This is just `getAuthClient().apiToken?.workspace ?? null`.
 */
export function getWorkspaceId(): string | null {
  return getAuthClient().apiToken?.workspace ?? null;
}

/**
 * React hook to get the current workspace.
 */
export function useWorkspaceId(): string | null {
  return useSyncExternalStore(getAuthClient().subscribe, getWorkspaceId);
}

/**
 * Check if the current user has a given feature switch enabled.
 * Returns false if not authenticated.
 */
export function getFeatureSwitch(name: FeatureSwitch): boolean {
  return getAuthClient().apiToken?.features?.includes(name) ?? false;
}

export function useFeatureSwitch(name: FeatureSwitch): boolean {
  const [value, setValue] = useState(() => getFeatureSwitch(name));
  useEffect(() => {
    const client = getAuthClient();
    return client.subscribe(() => setValue(getFeatureSwitch(name)));
  }, [name]);
  return value;
}

export async function refreshAuthState(): Promise<void> {
  const client = getAuthClient();
  await client.refreshApiToken();
}
